import {defineStore, skipHydrate, storeToRefs} from 'pinia';
import {
	type Gsoa_ShoppingList_GetAllShoppingLists_response as AllShoppingLists,
	type Gsoa_ShoppingList_GetAllShoppingListsCollection as ShoppingList,
	type Gsoa_ShoppingList_GetItemsCollection as ShoppingListItem,
	type Gsoa_ShoppingList_UpdateItem as UpdateItem,
	GsoaShoppingListService,
} from '../openapi';
import {shoppingListsBusKey} from '~/buses/shoppingLists';
import {createLocalShoppingListRepository} from '~/services/shoppingLists/LocalShoppingListRepository';
import {createRemoteShoppingListRepository} from '~/services/shoppingLists/RemoteShoppingListRepository';
import {
	createdAtItemKey,
	listCreatedAtKey,
	useShoppingListCreatedAtStorage,
	useShoppingListListCreatedAtStorage,
} from '~/services/shoppingLists/createdAtStorage';
import {
	buildSnapshotFromProduct,
	enrichGuestItems,
	getProductSnapshotCacheKey,
	needsProductEnrichment,
} from '~/services/shoppingLists/guestProductSnapshot';
import {clearGuestMergeState} from '~/services/shoppingLists/storage';
import type {
	CreateItemPayload,
	GuestProductSnapshot,
	ShoppingListItemsEntry,
} from '~/services/shoppingLists/types';
import {useCustomerProfileStore} from '~/stores/customerProfile';
import {useProductCatalogStore} from '~/stores/productCatalog';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {GLOBUS_HOME_GSOA_ID} from '#shared/constants';
import {getActiveShoppingListItemsCount, matchesShoppingListItemData} from '~/utils/shoppingList';
import {needsVisualOrderSync} from '~/services/shoppingLists/visualOrder';

interface ShoppingListItems {
	id: number
	products: ShoppingListItemsEntry['products']
}

type ShoppingListItemWithCreatedAt = ShoppingListItem & {CreatedAt?: string};

const DEFAULT_FALLBACK_HOUSE_NUMBER = GLOBUS_HOME_GSOA_ID;

export const useShoppingListsStore = defineStore('shoppingLists', () => {
	const customerProfileStore = useCustomerProfileStore();
	const productCatalogStore = useProductCatalogStore();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);

	const shoppingListsBus = import.meta.client ? useEventBus(shoppingListsBusKey) : undefined;
	const updateLoading = ref(false);
	const shoppingLists = ref<AllShoppingLists | null>(null);
	const shoppingListsItems = ref<ShoppingListItems[]>([]);
	const productDetailsByVanrAndHouse = ref(new Map<string, GuestProductSnapshot>());

	const houseNumber = computed(() => {
		const gsoaId = selectedHypermarketInstance.value?.content.gsoaId;
		return gsoaId ? Number(gsoaId) : DEFAULT_FALLBACK_HOUSE_NUMBER;
	});
	const getHouseNumber = () => houseNumber.value;

	const remoteRepository = createRemoteShoppingListRepository();
	const localRepository = createLocalShoppingListRepository();
	const createdAtByItemKey = import.meta.client
		? useShoppingListCreatedAtStorage()
		: ref<Record<string, string>>({});
	const createdAtByListId = import.meta.client
		? useShoppingListListCreatedAtStorage()
		: ref<Record<string, string>>({});

	const repository = computed(() => (userIsLogged.value ? remoteRepository : localRepository));

	const selectedShoppingList = computed(() => (
		shoppingLists.value?.Items?.find((item) => item.IsSelected)?.ID
		?? shoppingLists.value?.Items?.[0]?.ID
		?? null
	));
	const selectedShoppingListTotalCount = computed(() => {
		const id = selectedShoppingList.value;
		return id !== null ? getListActiveItemsCount(id) : null;
	});

	function getListActiveItemsCount(listId: number) {
		const itemsEntry = shoppingListsItems.value.find((entry) => entry.id === listId);
		if (itemsEntry) {
			return getActiveShoppingListItemsCount(itemsEntry.products.Items ?? []);
		}
		return shoppingLists.value?.Items?.find((list) => list.ID === listId)?.ItemsCount ?? 0;
	}

	function reset() {
		shoppingLists.value = null;
		shoppingListsItems.value = [];
		loadedListItemIds.value = new Set();
		productDetailsByVanrAndHouse.value.clear();
		remoteRepository.invalidateCache?.();
	}

	const loadedListItemIds = ref(new Set<number>());

	function isListItemsLoaded(listId: number) {
		return loadedListItemIds.value.has(listId);
	}

	function markListItemsLoaded(listId: number) {
		if (loadedListItemIds.value.has(listId)) {
			return;
		}
		loadedListItemIds.value = new Set([...loadedListItemIds.value, listId]);
	}

	function unmarkListItemsLoaded(listId: number) {
		if (!loadedListItemIds.value.has(listId)) {
			return;
		}
		const next = new Set(loadedListItemIds.value);
		next.delete(listId);
		loadedListItemIds.value = next;
	}

	function upsertListItemsEntry(entry: ShoppingListItems) {
		shoppingListsItems.value = [
			...shoppingListsItems.value.filter((item) => item.id !== entry.id),
			entry,
		];
	}

	function applyCachedSnapshot(item: ShoppingListItem, houseNumber: number) {
		const vanr = item.VANR ?? undefined;
		if (!vanr) {
			return;
		}
		const cacheKey = getProductSnapshotCacheKey(houseNumber, vanr);
		const cached = productDetailsByVanrAndHouse.value.get(cacheKey);
		if (!cached) {
			return;
		}

		if (!item.productDetail) {
			item.productDetail = cached;
			return;
		}

		item.productDetail = {
			...item.productDetail,
			...cached,
			calculatedPrice: {
				...item.productDetail.calculatedPrice,
				...cached.calculatedPrice,
			},
			productInHouse: item.productDetail.productInHouse ?? cached.productInHouse,
			imgIcon: item.productDetail.imgIcon ?? cached.imgIcon ?? undefined,
			imgDetail: item.productDetail.imgDetail ?? cached.imgDetail ?? undefined,
		};
	}

	function primeCacheFromItem(item: ShoppingListItem, houseNumber: number) {
		const vanr = item.VANR ?? undefined;
		if (!vanr || !item.productDetail) {
			return;
		}
		productDetailsByVanrAndHouse.value.set(
			getProductSnapshotCacheKey(houseNumber, vanr),
			item.productDetail,
		);
	}

	function getItemCreatedAt(listId: string | number, itemId: number) {
		return createdAtByItemKey.value[createdAtItemKey(listId, itemId)];
	}

	function recordItemCreatedAt(listId: string | number, itemId: number, createdAt: string) {
		if (!import.meta.client) {
			return;
		}
		createdAtByItemKey.value = {
			...createdAtByItemKey.value,
			[createdAtItemKey(listId, itemId)]: createdAt,
		};
	}

	function removeItemCreatedAt(listId: string | number, itemId: number) {
		if (!import.meta.client) {
			return;
		}
		const key = createdAtItemKey(listId, itemId);
		if (!createdAtByItemKey.value[key]) {
			return;
		}
		const next = {...createdAtByItemKey.value};
		delete next[key];
		createdAtByItemKey.value = next;
	}

	function recordListCreatedAt(listId: string | number, createdAt: string) {
		if (!import.meta.client) {
			return;
		}
		createdAtByListId.value = {
			...createdAtByListId.value,
			[listCreatedAtKey(listId)]: createdAt,
		};
	}

	function getListCreatedAt(listId: string | number): string | undefined {
		const stored = createdAtByListId.value[listCreatedAtKey(listId)];
		if (stored) {
			return stored;
		}

		if (!userIsLogged.value) {
			const guestList = localRepository.storage.value.lists.find((list) => list.ID === Number(listId));
			return guestList?.CreatedAt;
		}

		return undefined;
	}

	function enrichItemsWithCreatedAt(listId: string | number, items: ShoppingListItem[]) {
		for (const item of items) {
			const itemWithCreatedAt = item as ShoppingListItemWithCreatedAt;
			const existing = itemWithCreatedAt.CreatedAt ?? getItemCreatedAt(listId, item.ID);
			if (existing) {
				itemWithCreatedAt.CreatedAt = existing;
				continue;
			}
			const fallback = item.LastUpdate ?? new Date().toISOString();
			itemWithCreatedAt.CreatedAt = fallback;
			if (userIsLogged.value) {
				recordItemCreatedAt(listId, item.ID, fallback);
			}
		}
	}

	function enrichEntriesWithCreatedAt(entries: ShoppingListItemsEntry[]) {
		for (const entry of entries) {
			if (entry.products.Items?.length) {
				enrichItemsWithCreatedAt(entry.id, entry.products.Items);
			}
		}
	}

	async function enrichEntries(entries: ShoppingListItemsEntry[]) {
		const houseNumber = getHouseNumber();
		if (houseNumber) {
			const allItems = entries.flatMap((entry) => entry.products.Items ?? []);
			for (const item of allItems) {
				primeCacheFromItem(item, houseNumber);
				applyCachedSnapshot(item, houseNumber);
			}
			try {
				await enrichGuestItems(allItems, houseNumber, {cache: productDetailsByVanrAndHouse.value});
			} catch {
				// Product enrichment (prices, images, ...) is optional and best-effort; the list
				// content itself must still render even if the catalog lookup fails or is slow.
			}
		}
		enrichEntriesWithCreatedAt(entries);
	}

	async function loadGuestListsAndItems(lists: AllShoppingLists) {
		const items = await localRepository.fetchItems();
		await enrichEntries(items);
		shoppingListsItems.value = items.map((entry) => ({id: entry.id, products: entry.products}));
		for (const entry of items) {
			markListItemsLoaded(entry.id);
		}
		shoppingLists.value = lists;
	}

	async function refreshGuestState() {
		if (userIsLogged.value) {
			return;
		}
		const lists = await localRepository.fetchLists();
		if (!lists) {
			return;
		}
		await loadGuestListsAndItems(lists);
	}

	if (import.meta.client) {
		watch(() => localRepository.storage.value.lastModified, () => {
			if (!userIsLogged.value) {
				refreshGuestState();
			}
		});
		watch(houseNumber, async (value, oldValue) => {
			if (!value || value === oldValue || shoppingListsItems.value.length === 0) {
				return;
			}
			if (userIsLogged.value) {
				const listIds = [...loadedListItemIds.value];
				await Promise.all(
					listIds.map((listId) => fetchShoppingListItems(String(listId), {force: true})),
				);
				return;
			}
			await enrichEntries(shoppingListsItems.value);
			shoppingListsItems.value = [...shoppingListsItems.value];
		});

		watch(userIsLogged, async (logged, wasLogged) => {
			if (wasLogged && !logged) {
				clearGuestMergeState();
				reset();
				await refreshGuestState();
				return;
			}
			if (logged && !wasLogged) {
				reset();
				await fetchShoppingLists();
				await ensureSelectedListItems();
			}
		});

		watch(selectedShoppingList, async (listId, previousListId) => {
			if (listId == null || listId === previousListId || !userIsLogged.value) {
				return;
			}
			await fetchShoppingListItems(String(listId));
		});
	}

	function clearCachedShoppingLists() {
		shoppingLists.value = null;
		shoppingListsItems.value = [];
	}

	async function fetchShoppingList(shoppingListId: string) {
		return await GsoaShoppingListService.getShoppingList({shoppingListId});
	}

	// Concurrent callers (e.g. a page's useAsyncData plus a store watcher firing at the same time)
	// share a single in-flight request instead of racing separate reads of localStorage/the API.
	// A `force` call never joins an existing non-forced in-flight request - it always starts its
	// own, so callers that explicitly ask for fresh data can't silently get a stale result.
	let fetchShoppingListsPromise: Promise<AllShoppingLists | null> | null = null;

	async function fetchShoppingLists(options?: {force?: boolean}) {
		const force = options?.force ?? false;

		if (shoppingLists.value && !force) {
			return shoppingLists.value;
		}

		if (fetchShoppingListsPromise && !force) {
			return fetchShoppingListsPromise;
		}

		fetchShoppingListsPromise = (async () => {
			const lists = await repository.value.fetchLists(force || !userIsLogged.value);

			if (!userIsLogged.value) {
				if (lists) {
					await loadGuestListsAndItems(lists);
				}
				return lists;
			}

			if (lists) {
				shoppingLists.value = lists;
			}
			return lists;
		})();

		try {
			return await fetchShoppingListsPromise;
		} finally {
			fetchShoppingListsPromise = null;
		}
	}

	async function deleteShoppingList(listId: string) {
		const success = await repository.value.deleteList(listId);
		if (success && shoppingLists.value?.Items) {
			const nextItems = shoppingLists.value.Items.filter((list) => list.ID.toString() !== listId);
			shoppingLists.value = {
				...shoppingLists.value,
				Items: nextItems,
				totalCount: nextItems.length,
			};
			shoppingListsItems.value = shoppingListsItems.value.filter((item) => item.id.toString() !== listId);
			unmarkListItemsLoaded(Number(listId));
		}
	}

	async function updateShoppingList(listId: string, name: string, IsSelected?: boolean) {
		const response = await repository.value.updateList(listId, name, IsSelected);
		if (response && shoppingLists.value?.Items) {
			const nextItems = shoppingLists.value.Items.map((list) => {
				if (list.ID !== response.ID) {
					return IsSelected ? {...list, IsSelected: false} : list;
				}
				return {
					...list,
					Name: response.Name!.toString(),
					IsSelected: IsSelected ? true : list.IsSelected,
				};
			});
			shoppingLists.value = {
				...shoppingLists.value,
				Items: nextItems,
			};
		}
	}

	async function createShoppingList(name: string) {
		const response = await repository.value.createList(name);
		if (response?.ID) {
			recordListCreatedAt(response.ID, new Date().toISOString());
			const current = shoppingLists.value ?? {Items: [], totalCount: 0};
			const nextItems = [...(current.Items ?? []), response as ShoppingList];
			shoppingLists.value = {
				...current,
				Items: nextItems,
				totalCount: nextItems.length,
			};
			shoppingListsItems.value = [...shoppingListsItems.value, {id: response.ID, products: {Items: []}}];
		}
		return response;
	}

	function syncListItemsCount(listId: number) {
		const count = getListActiveItemsCount(listId);
		const summaryItem = shoppingLists.value?.Items?.find((list) => list.ID === listId);
		if (summaryItem) {
			summaryItem.ItemsCount = count;
		}
	}

	async function fetchShoppingListItems(id?: string, options?: {force?: boolean}) {
		const force = options?.force ?? false;

		if (shoppingLists.value === null) {
			await fetchShoppingLists();
		}

		const resolvedId = id ?? (selectedShoppingList.value != null ? String(selectedShoppingList.value) : undefined);
		if (!resolvedId) {
			return shoppingListsItems.value;
		}

		const listId = Number(resolvedId);
		if (!Number.isFinite(listId)) {
			return shoppingListsItems.value;
		}

		if (!force && isListItemsLoaded(listId)) {
			const cachedEntry = shoppingListsItems.value.find((entry) => entry.id === listId);
			if (cachedEntry) {
				await enrichEntries([cachedEntry]);
			}
			return shoppingListsItems.value;
		}

		const entries = await repository.value.fetchItems(
			resolvedId,
			userIsLogged.value ? getHouseNumber() : undefined,
		);
		await enrichEntries(entries);

		const newItem = entries.find((entry) => entry.id === listId);
		if (newItem) {
			upsertListItemsEntry({id: newItem.id, products: newItem.products});
			syncListItemsCount(listId);
			markListItemsLoaded(listId);
		}

		return shoppingListsItems.value;
	}

	function getShoppingListItemsByListId(listId: number) {
		return shoppingListsItems.value.find((list) => list.id === listId)?.products.Items;
	}

	async function ensureSelectedListItems(options?: {force?: boolean}) {
		if (shoppingLists.value === null) {
			await fetchShoppingLists();
		}

		if (!userIsLogged.value) {
			return shoppingListsItems.value;
		}

		const listId = selectedShoppingList.value;
		if (listId == null) {
			return shoppingListsItems.value;
		}

		return fetchShoppingListItems(String(listId), options);
	}

	function findListInState(listId: number) {
		return shoppingLists.value?.Items?.some((list) => list.ID === listId);
	}

	async function createNewItem(shoppingListId: string, itemData: UpdateItem, product?: CreateItemPayload['product']) {
		const payload: CreateItemPayload = {...itemData, product};

		if (product) {
			const houseNumber = getHouseNumber();
			if (houseNumber) {
				await productCatalogStore.fetchCategories();
				const mainCategory = productCatalogStore.resolveMainCategory(product.productCategories);
				const snapshot = buildSnapshotFromProduct(product, houseNumber, mainCategory);
				if (!userIsLogged.value) {
					payload.snapshot = snapshot;
				}
				const vanr = itemData.VANR ?? undefined;
				if (vanr) {
					productDetailsByVanrAndHouse.value.set(
						getProductSnapshotCacheKey(houseNumber, vanr),
						snapshot,
					);
				}
			}
		}

		let effectiveListId = shoppingListId;
		if (!effectiveListId || !findListInState(Number(effectiveListId))) {
			await fetchShoppingLists();
			effectiveListId = selectedShoppingList.value?.toString() ?? effectiveListId;
		}

		const response = await repository.value.createItem(effectiveListId, payload);

		if (response?.ID) {
			let listId = Number(effectiveListId);
			if (!userIsLogged.value && !findListInState(listId)) {
				await fetchShoppingLists();
				listId = selectedShoppingList.value ?? listId;
			}

			const existingShoppingList = shoppingListsItems.value.find((item) => item.id.toString() === listId.toString());

			if (existingShoppingList) {
				if (!existingShoppingList.products.Items) {
					existingShoppingList.products.Items = [];
				}
				const existingItem = existingShoppingList.products.Items.find((p) => matchesShoppingListItemData(p, itemData));
				if (existingItem) {
					existingItem.Amount = response.Amount;
					const currentHouseNumber = getHouseNumber();
					if (currentHouseNumber) {
						applyCachedSnapshot(existingItem, currentHouseNumber);
					}
				} else {
					const currentHouseNumber = getHouseNumber();
					if (currentHouseNumber) {
						applyCachedSnapshot(response, currentHouseNumber);
					}
					const itemWithCreatedAt = response as ShoppingListItemWithCreatedAt;
					const createdAt = itemWithCreatedAt.CreatedAt ?? new Date().toISOString();
					itemWithCreatedAt.CreatedAt = createdAt;
					if (userIsLogged.value) {
						recordItemCreatedAt(listId, response.ID, createdAt);
					}
					existingShoppingList.products.Items.push(response);
				}
			} else {
				const currentHouseNumber = getHouseNumber();
				if (currentHouseNumber) {
					applyCachedSnapshot(response, currentHouseNumber);
				}
				const itemWithCreatedAt = response as ShoppingListItemWithCreatedAt;
				const createdAt = itemWithCreatedAt.CreatedAt ?? new Date().toISOString();
				itemWithCreatedAt.CreatedAt = createdAt;
				if (userIsLogged.value) {
					recordItemCreatedAt(listId, response.ID, createdAt);
				}
				shoppingListsItems.value.push({id: listId, products: {Items: [response]}});
			}

			syncListItemsCount(listId);

			const vanr = itemData.VANR ?? undefined;
			if (vanr) {
				const currentHouseNumber = getHouseNumber();
				if (currentHouseNumber) {
					const listEntry = shoppingListsItems.value.find((entry) => entry.id === listId);
					const listItem = listEntry?.products.Items?.find((p) => matchesShoppingListItemData(p, itemData));
					if (listItem && needsProductEnrichment(listItem, currentHouseNumber)) {
						await enrichGuestItems([listItem], currentHouseNumber, {cache: productDetailsByVanrAndHouse.value});
					}
				}
				shoppingListsBus?.emit({type: 'add', shoppingListId: listId.toString(), productId: vanr});
			}
		}
	}

	async function clearShoppingListItems(shoppingListId: string) {
		await repository.value.clearItems(shoppingListId);

		shoppingListsItems.value.forEach((item) => {
			if (item.id.toString() === shoppingListId) {
				item.products.Items = [];
			}
		});

		syncListItemsCount(Number(shoppingListId));
	}

	async function deleteShoppingListItem(shoppingListId: string, itemId: string) {
		await repository.value.deleteItem(shoppingListId, itemId);

		for (const item of shoppingListsItems.value) {
			if (item.id.toString() === shoppingListId) {
				const listItemIndex = item.products.Items.findIndex((product) => product.ID.toString() === itemId);

				if (listItemIndex !== -1) {
					const listItem = item.products.Items[listItemIndex];
					item.products.Items.splice(listItemIndex, 1);
					removeItemCreatedAt(shoppingListId, listItem.ID);

					if (listItem.VANR) {
						shoppingListsBus?.emit({type: 'remove', shoppingListId, productId: listItem.VANR});
					}
				}
				break;
			}
		}

		syncListItemsCount(Number(shoppingListId));
	}

	async function setAmountShoppingListItem(shoppingListId: string, itemId: number, requestBody: UpdateItem) {
		try {
			updateLoading.value = true;

			const response = await repository.value.updateItem(shoppingListId, String(itemId), requestBody);

			if (!response) {
				return;
			}

			const item = findItemInShoppingList(shoppingListId, itemId);

			if (item) {
				item.ID = response.ID;
				item.Amount = response.Amount;
			}

			syncListItemsCount(Number(shoppingListId));
		} catch (e: any) {
			throw createError({
				message: 'Failed to update shopping list item',
				data: {shoppingListId, itemId, requestBody},
				cause: e,
			});
		} finally {
			updateLoading.value = false;
		}
	}

	async function setPurchasedShoppingListItem(shoppingListId: string, itemId: number, purchased: boolean) {
		try {
			updateLoading.value = true;

			const item = findItemInShoppingList(shoppingListId, itemId);
			const response = await repository.value.updateItem(shoppingListId, String(itemId), {
				Text: item?.Text ?? '',
				Amount: item?.Amount ?? 1,
				Purchased: purchased,
			});

			if (!response) {
				return;
			}

			const updatedItem = findItemInShoppingList(shoppingListId, itemId);

			if (updatedItem) {
				updatedItem.ID = response.ID;
				updatedItem.Purchased = response.Purchased ?? purchased;
				updatedItem.Amount = response.Amount;
			}

			syncListItemsCount(Number(shoppingListId));
		} catch (e: any) {
			throw createError({
				message: 'Failed to update shopping list item purchased state',
				data: {shoppingListId, itemId, purchased},
				cause: e,
			});
		} finally {
			updateLoading.value = false;
		}
	}

	async function deleteShoppingListItems(shoppingListId: string, itemIds: number[]) {
		for (const itemId of itemIds) {
			await deleteShoppingListItem(shoppingListId, String(itemId));
		}
	}

	async function updateShoppingListItemText(shoppingListId: string, itemId: number, text: string) {
		try {
			updateLoading.value = true;

			const item = findItemInShoppingList(shoppingListId, itemId);
			const response = await repository.value.updateItem(shoppingListId, String(itemId), {
				Text: text,
				Amount: item?.Amount ?? 1,
			});

			if (!response) {
				return;
			}

			const updatedItem = findItemInShoppingList(shoppingListId, itemId);

			if (updatedItem) {
				updatedItem.ID = response.ID;
				updatedItem.Text = response.Text;
				updatedItem.Amount = response.Amount;
			}
		} catch (e: any) {
			throw createError({
				message: 'Failed to update shopping list item text',
				data: {shoppingListId, itemId, text},
				cause: e,
			});
		} finally {
			updateLoading.value = false;
		}
	}

	async function setSelectedShoppingList(shoppingListId: string, Name: string) {
		try {
			const response = await repository.value.updateList(shoppingListId, Name, true);

			if (response?.ID && shoppingLists.value?.Items) {
				shoppingLists.value = {
					...shoppingLists.value,
					Items: shoppingLists.value.Items.map((item) => ({
						...item,
						IsSelected: item.ID === response.ID,
					})),
				};
				return true;
			}

			return false;
		} catch (e: any) {
			throw createError({
				message: 'Failed to set selected shopping list',
				data: {shoppingListId, Name},
				cause: e,
			});
		}
	}

	function findItemInShoppingList(shoppingListId: string, itemId: number) {
		const shoppingList = shoppingListsItems.value.find((list) => list.id === Number(shoppingListId));
		return shoppingList?.products.Items.find((product) => product.ID === itemId);
	}

	async function reorderShoppingListItems(shoppingListId: string, orderedItemIds: number[]) {
		const shoppingList = shoppingListsItems.value.find((list) => list.id === Number(shoppingListId));
		const sourceItems = shoppingList?.products.Items;

		if (!shoppingList || !sourceItems?.length) {
			await repository.value.reorderItems(
				shoppingListId,
				orderedItemIds,
				undefined,
				userIsLogged.value ? getHouseNumber() : undefined,
			);
			return;
		}

		const itemsBeforeReorder = sourceItems.map((item) => ({...item}));

		if (!needsVisualOrderSync(itemsBeforeReorder, orderedItemIds)) {
			return;
		}

		const itemsById = new Map(sourceItems.map((item) => [item.ID, item]));
		const reordered: typeof sourceItems = [];

		orderedItemIds.forEach((itemId) => {
			const item = itemsById.get(itemId);
			if (item) {
				reordered.push(item);
				item.VisualOrder = reordered.length;
				itemsById.delete(itemId);
			}
		});

		itemsById.forEach((item) => {
			reordered.push(item);
			item.VisualOrder = reordered.length;
		});

		shoppingList.products.Items = reordered;
		shoppingListsItems.value = [...shoppingListsItems.value];

		await repository.value.reorderItems(
			shoppingListId,
			orderedItemIds,
			itemsBeforeReorder,
			userIsLogged.value ? getHouseNumber() : undefined,
		);
	}

	return {
		selectedShoppingList,
		selectedShoppingListTotalCount,
		clearCachedShoppingLists,
		fetchShoppingList,
		fetchShoppingLists,
		fetchShoppingListItems,
		getShoppingListItemsByListId,
		ensureSelectedListItems,
		shoppingLists: skipHydrate(shoppingLists),
		shoppingListsItems: skipHydrate(shoppingListsItems),
		productDetailsByVanrAndHouse: skipHydrate(productDetailsByVanrAndHouse),
		createNewItem,
		clearShoppingListItems,
		deleteShoppingListItem,
		reorderShoppingListItems,
		setAmountShoppingListItem,
		setPurchasedShoppingListItem,
		updateShoppingListItemText,
		deleteShoppingListItems,
		setSelectedShoppingList,
		createShoppingList,
		deleteShoppingList,
		updateShoppingList,
		reset,
		syncListItemsCount,
		getListActiveItemsCount,
		remoteRepository,
		clearGuestStorage: () => localRepository.clearAll(),
		getGuestListsState: () => localRepository.storage.value,
		recordItemCreatedAt,
		getItemCreatedAt,
		recordListCreatedAt,
		getListCreatedAt,
		removeGuestList: (listId: number) => {
			const index = localRepository.storage.value.lists.findIndex((l) => l.ID === listId);
			if (index !== -1) {
				localRepository.storage.value.lists.splice(index, 1);
			}
		},
		removeGuestItem: (listId: number, itemId: number) => {
			const list = localRepository.storage.value.lists.find((l) => l.ID === listId);
			if (!list) {
				return;
			}
			const itemIndex = list.items.findIndex((i) => i.ID === itemId);
			if (itemIndex !== -1) {
				list.items.splice(itemIndex, 1);
				list.ItemsCount = getActiveShoppingListItemsCount(list.items);

				const itemsEntry = shoppingListsItems.value.find((entry) => entry.id === listId);
				if (itemsEntry) {
					const stateIndex = itemsEntry.products.Items.findIndex((i) => i.ID === itemId);
					if (stateIndex !== -1) {
						itemsEntry.products.Items.splice(stateIndex, 1);
					}
				}

				syncListItemsCount(listId);
			}
		},
	};
});
